/**
 * notificationPrefs.js — single source of truth for notification routes
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Vinarai sends users many kinds of notifications: WOTD picks, nearby shop
 * activity, event invitations, cancellations, marketing pings, etc. Each
 * route is independently controllable on TWO channels:
 *
 *   • push   — native iOS banner (delivered via FCM, gated by APNs)
 *   • inApp  — red-dot badge on the relevant nav surface + Notifications
 *              inbox row (no banner)
 *
 * A few routes (event cancellations, claimed invitations) are flagged
 * `alwaysOn` because turning them off would surprise the user in a
 * bad way ("my friend showed up and the event was canceled, why didn't
 * I get told?"). Those are still toggleable per channel, but the master
 * switch is locked.
 *
 * Persistence:
 *   profiles/{uid}.notificationPrefs = {
 *     wotd:                { push: true,  inApp: true },
 *     localShopNews:       { push: true,  inApp: true, radiusKm: 25 },
 *     wineMatchNearby:     { push: true,  inApp: true },
 *     eventInvitations:    { push: true,  inApp: true },
 *     eventReminders:      { push: true,  inApp: true, hoursAhead: 24 },
 *     eventChanges:        { push: true,  inApp: true },
 *     followedShopUpdates: { push: false, inApp: true },
 *     followedShopTastings:{ push: true,  inApp: true },
 *     friendInvites:       { push: true,  inApp: true },
 *     productUpdates:      { push: false, inApp: true },
 *     marketing:           { push: false, inApp: false },
 *     quietHours: { enabled: false, start: '22:00', end: '07:00' },
 *   }
 *
 * Server-side dispatcher contract (TODO once cloud functions land):
 *   Every push send MUST read profiles/{uid}.notificationPrefs and gate
 *   on `prefs[routeKey].push === true` before adding the user's FCM
 *   token to the multicast. Same for in-app: writes to
 *   notifications/{uid}/items only happen when `prefs[routeKey].inApp`
 *   is true. The dispatcher should also honor quietHours by deferring
 *   non-critical pushes until quietHours.end.
 *
 * Always-on routes still send on both channels regardless of pref —
 * but the user can DISABLE iOS push at the OS level via Settings →
 * Vinarai → Notifications, which we can't override and shouldn't try to.
 */

import { doc, setDoc } from 'firebase/firestore'
import { db } from '../firebase.js'

// ── Route catalog ────────────────────────────────────────────────────────
// Each entry defines:
//   key       — Firestore field name under notificationPrefs
//   label     — human-readable title shown in the UI
//   blurb     — one-line description below the label
//   category  — grouping for the settings page sections
//   icon      — emoji-style glyph identifier (consumed by the page render)
//   defaults  — initial { push, inApp, ...extras } when no profile pref
//   alwaysOn  — if true, master switch is locked (per-channel still toggleable)
//   serverGated — narrative hint to the future dispatcher about WHO sends
//                 it (consumer-facing routes the user controls, vs system
//                 critical routes like billing receipts which aren't here)
//
// Adding a new route: append here, set defaults, then update the server-
// side dispatcher to read the new key before sending. The settings page
// auto-picks up new entries — no UI change needed.
export const NOTIFICATION_ROUTES = [
  // ── Wine discovery ─────────────────────────────────────────────────
  {
    key: 'wotd',
    label: 'Wine of the Day',
    blurb: 'Daily personalized pick from your cellar or matches.',
    category: 'discovery',
    icon: 'wotd',
    // VIN-2026-14 (2026-05-26): localHour replaces the hardcoded 5pm
    // (17:00) schedule in the WOTD Cloud Function. Server reads
    // profile.notificationPrefs.wotd.localHour at dispatch time and
    // computes the next-fire UTC time from the user's stored timezone.
    defaults: { push: true, inApp: true, localHour: 17 },
    extras: { localHour: true },
  },
  {
    key: 'localShopNews',
    label: 'Local Shop News',
    blurb: 'Updates from wine shops within your radius.',
    category: 'discovery',
    icon: 'pin',
    defaults: { push: true, inApp: true, radiusKm: 40 }, // ~25 mi
    extras: { radius: true },
  },
  {
    key: 'wineMatchNearby',
    label: 'Wine Matches Near You',
    blurb: 'When a nearby shop stocks wines that fit your taste profile.',
    category: 'discovery',
    icon: 'sparkle',
    // VIN-2026-14: tighter default radius than localShopNews (people
    // drive further for shops than for "wine I'd buy right now").
    defaults: { push: true, inApp: true, radiusKm: 40 }, // ~25 mi
    extras: { radius: true },
  },

  // ── Events & tickets ───────────────────────────────────────────────
  {
    key: 'eventInvitations',
    label: 'Event Invitations',
    blurb: 'Invites from friends and the shops you follow.',
    category: 'events',
    icon: 'envelope',
    defaults: { push: true, inApp: true },
  },
  {
    key: 'eventReminders',
    label: 'Day-of Reminders',
    blurb: 'A nudge before an event you said you would attend.',
    category: 'events',
    icon: 'clock',
    defaults: { push: true, inApp: true, hoursAhead: 24 },
    extras: { hoursAhead: true },
  },
  {
    key: 'eventChanges',
    label: 'Event Changes',
    blurb: 'Cancellations, time changes, venue updates.',
    category: 'events',
    icon: 'warning',
    defaults: { push: true, inApp: true },
    alwaysOn: true, // safety-critical — can't fully suppress
  },

  // ── Followed shops ─────────────────────────────────────────────────
  {
    key: 'followedShopTastings',
    label: 'New Tastings',
    blurb: 'When a shop you follow posts a new tasting event.',
    category: 'followed',
    icon: 'bottle',
    defaults: { push: true, inApp: true },
  },
  {
    key: 'followedShopUpdates',
    label: 'Shop News',
    blurb: 'Story posts, inventory drops, and announcements.',
    category: 'followed',
    icon: 'newspaper',
    defaults: { push: false, inApp: true }, // less critical — opt-in for push
  },

  // ── Social ─────────────────────────────────────────────────────────
  {
    key: 'friendInvites',
    label: 'Friend & Household Invites',
    blurb: 'Connection requests and household-cellar invitations.',
    category: 'social',
    icon: 'people',
    defaults: { push: true, inApp: true },
    alwaysOn: true, // social safety: people accepting your invite need to land
  },
  {
    // Friend rated a wine highly that fits your taste profile. Fires
    // when a connected friend posts a rating ≥ 90 on a wine whose
    // varietal/region matches the user's onboarding preferences.
    // Dispatcher: server-side cron after each rating write, matches
    // against the rater's connections, filters by taste-affinity score.
    key: 'friendHighRating',
    label: 'Friend Rated a Match',
    blurb: 'When a friend rates a wine highly that fits your taste.',
    category: 'social',
    icon: 'star',
    // VIN-2026-14 (2026-05-26): minScore lets users tune signal-to-
    // noise. Default 90 matches the dispatcher's existing hardcoded
    // threshold. Power users with active circles may want 95; lighter
    // users can drop to 85.
    defaults: { push: true, inApp: true, minScore: 90 },
    extras: { minScore: true },
  },
  {
    // Household member added a wine to the shared cellar whose
    // varietal/region matches the user's taste. Higher signal than
    // friendHighRating because it's a wine actually in the user's
    // physical reach.
    key: 'householdNewMatch',
    label: 'Household Added a Match',
    blurb: 'When a household member adds a wine that fits your taste.',
    category: 'social',
    icon: 'home',
    defaults: { push: true, inApp: true },
  },

  // ── From Vinarai ───────────────────────────────────────────────────
  {
    key: 'productUpdates',
    label: 'Product Updates',
    blurb: 'New features, AI improvements, and release notes.',
    category: 'vinarai',
    icon: 'star',
    defaults: { push: false, inApp: true },
  },
  {
    key: 'marketing',
    label: 'Marketing & Tips',
    blurb: 'Wine education, partner offers, occasional promos.',
    category: 'vinarai',
    icon: 'megaphone',
    defaults: { push: false, inApp: false }, // strict opt-in
  },

  // ── Digests ────────────────────────────────────────────────────────
  // Periodic recaps of social activity. Single route with a cadence
  // picker rather than two separate routes (weekly+monthly) so the
  // user can shift between cadences without losing their channel
  // preferences. cadence='off' is the same as toggling both channels
  // off — kept distinct in storage so the dispatcher can tell "user
  // doesn't want a digest" from "user hasn't set this yet (use defaults)."
  {
    key: 'socialDigest',
    label: 'Friends & Household Recap',
    blurb: 'A summary of what your circle tasted, rated, and added.',
    category: 'digests',
    icon: 'newspaper',
    defaults: { push: false, inApp: true, cadence: 'weekly' },
    extras: { cadence: true },
  },
]

// ── Category metadata ─────────────────────────────────────────────────
// Order here = section order on the settings page.
export const CATEGORIES = [
  { key: 'discovery', label: 'Wine Discovery',      description: 'Find new wines you’ll love.' },
  { key: 'events',    label: 'Events & Tickets',    description: 'Invites and reminders.' },
  { key: 'followed',  label: 'Followed Shops',      description: 'Shops you follow notify you here.' },
  { key: 'social',    label: 'Friends & Household', description: 'People-driven activity.' },
  { key: 'digests',   label: 'Recaps',              description: 'Periodic summaries you can tune to weekly or monthly.' },
  { key: 'vinarai',   label: 'From Vinarai',        description: 'About the app itself.' },
]

// ── Defaults builder ──────────────────────────────────────────────────
// Builds the initial notificationPrefs object — call once when a profile
// is first created (or used as fallback when reading a profile that
// predates this schema).
//
// VIN-2026-19 (2026-05-26): removed `marketingPushOk` master gate.
// It was added earlier as belt-and-suspenders App Store compliance,
// but turned out to be redundant in practice — the per-route push
// toggles (productUpdates.push, marketing.push) are already strict
// opt-in (default false) and that single layer satisfies Apple's
// "non-transactional push must be opt-in" requirement. The master
// gate just confused users with a third toggle that did the same
// thing as the per-route ones. The field may still exist on legacy
// profiles; mergeWithDefaults silently ignores it.
export function buildDefaultNotificationPrefs() {
  const prefs = {
    quietHours: { enabled: false, start: '22:00', end: '07:00' },
  }
  for (const route of NOTIFICATION_ROUTES) {
    prefs[route.key] = { ...route.defaults }
  }
  return prefs
}

// ── Merge helper ──────────────────────────────────────────────────────
// Merge a partial pref read from Firestore over the route defaults so
// any new routes added post-launch get sensible values without a
// migration. Always-on routes stay master-on even if the stored value
// says off (defensive — protects against malformed writes).
export function mergeWithDefaults(stored) {
  const defaults = buildDefaultNotificationPrefs()
  if (!stored || typeof stored !== 'object') return defaults
  const merged = { ...defaults }
  for (const route of NOTIFICATION_ROUTES) {
    const s = stored[route.key]
    if (!s || typeof s !== 'object') continue
    merged[route.key] = { ...route.defaults, ...s }
    if (route.alwaysOn) {
      // alwaysOn routes: at least one channel must be active. If the
      // user (or bad data) turned both off, fall back to inApp.
      const m = merged[route.key]
      if (!m.push && !m.inApp) m.inApp = true
    }
  }
  if (stored.quietHours && typeof stored.quietHours === 'object') {
    merged.quietHours = { ...defaults.quietHours, ...stored.quietHours }
  }
  return merged
}

// ── Persistence ───────────────────────────────────────────────────────
// Patch a single route's channel — minimal write, doesn't touch the
// other routes. Returns the new pref object so the caller's UI can
// optimistically reflect the change without re-reading Firestore.
export async function setRouteChannel(uid, routeKey, channel, value) {
  if (!uid || !routeKey || (channel !== 'push' && channel !== 'inApp')) return
  const ref = doc(db, 'profiles', uid)
  // Dot-path notation lets us write a nested field without overwriting
  // sibling fields. Firestore supports this via { merge: true }.
  await setDoc(ref, {
    notificationPrefs: {
      [routeKey]: { [channel]: !!value },
    },
  }, { merge: true })
}

// Patch a route's extras (radius for location-based, hoursAhead for
// reminders). Same merge semantics as setRouteChannel.
export async function setRouteExtra(uid, routeKey, extraKey, value) {
  if (!uid || !routeKey || !extraKey) return
  await setDoc(doc(db, 'profiles', uid), {
    notificationPrefs: {
      [routeKey]: { [extraKey]: value },
    },
  }, { merge: true })
}

// Patch quietHours.
export async function setQuietHours(uid, patch) {
  if (!uid) return
  await setDoc(doc(db, 'profiles', uid), {
    notificationPrefs: { quietHours: patch },
  }, { merge: true })
}

// VIN-2026-19 (2026-05-26): setMarketingPushOk removed. The master
// gate was redundant with per-route push toggles for productUpdates
// + marketing (both default push:false). The per-route layer is the
// single source of truth now.

// Group routes by category for the page renderer — preserves the order
// from NOTIFICATION_ROUTES so adding a new route slots it naturally.
export function groupRoutesByCategory() {
  const grouped = {}
  for (const route of NOTIFICATION_ROUTES) {
    if (!grouped[route.category]) grouped[route.category] = []
    grouped[route.category].push(route)
  }
  return grouped
}
